// src/components/NoteCommessa.js
// ═══════════════════════════════════════════════════════════════════════════
// LE MIE NOTE DELLA COMMESSA (post-it personali, ottobre 2026)
// Le stesse note si leggono tutte insieme nel «Registro delle mie note» (pages/
// RegistroNotePage.js), dove ci sono anche le note generali (senza commessa).
// Appunti veloci che non sono attività: ognuno vede e scrive SOLO i propri. Lo impone il
// database (firestore rules 54, match /noteAppunti: lettura e scrittura solo per uid),
// non solo questa schermata — nessuna eccezione per titolare, admin o Platform Owner.
// Le note non entrano in ricerca globale, registro eventi, Cabina di regia, Insight,
// esportazioni o notifiche: qui sotto non si chiama logEvento di proposito.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useRef, useState } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { X, Plus, Pin, Check, Trash2, RotateCcw, Lock } from "lucide-react";
import PromemoriaNota from "./PromemoriaNota";

export const COLORI = {
  giallo: { bg: "#fef9c3", bordo: "#fde047" },
  verde:  { bg: "#dcfce7", bordo: "#86efac" },
  azzurro:{ bg: "#dbeafe", bordo: "#93c5fd" },
  rosa:   { bg: "#fce7f3", bordo: "#f9a8d4" },
  viola:  { bg: "#ede9fe", bordo: "#c4b5fd" },
  // W406: le note importanti da tenere sotto osservazione (in cima, in Home e con promemoria)
  rosso:  { bg: "#fee2e2", bordo: "#f87171" },
};
const NOMI_COLORE = { giallo: "Giallo", verde: "Verde", azzurro: "Azzurro", rosa: "Rosa", viola: "Viola", rosso: "Rosso — importante, con promemoria" };
const MAX_TESTO = 5000;

export const ms = t => (t?.toMillis ? t.toMillis() : 0);
// in cima le note fissate, poi le importanti (rosse), poi le più recenti
export const ordina = (a, b) => (b.fissata ? 1 : 0) - (a.fissata ? 1 : 0) || (b.colore === "rosso" ? 1 : 0) - (a.colore === "rosso" ? 1 : 0) || ms(b.createdAt) - ms(a.createdAt);
const quando = t => {
  if (!t?.toDate) return "";
  const d = t.toDate();
  return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" }) + " " + d.toTimeString().slice(0, 5);
};

export function PostIt({ nota, onSalva, onAggiorna, onElimina, autoFocus, sotto = null }) {
  const [testo, setTesto] = useState(nota.testo || "");
  const salvato = useRef(nota.testo || "");
  const testoRef = useRef(nota.testo || "");
  testoRef.current = testo;
  const timer = useRef(null);
  const colore = COLORI[nota.colore] || COLORI.giallo;
  // «⤢»: la nota a tutto schermo con il testo grande (comodo sul telefono); «⤡», Esc o il tasto
  // indietro la riportano com'era (W407)
  const [grande, setGrande] = useState(false);
  useEffect(() => {
    if (!grande) return undefined;
    const esc = (e) => { if (e.key === "Escape") setGrande(false); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [grande]);

  // Se la nota cambia da un altro dispositivo e qui non la si sta scrivendo, si aggiorna
  useEffect(() => {
    const remoto = nota.testo || "";
    if (testoRef.current === salvato.current && remoto !== salvato.current) { setTesto(remoto); salvato.current = remoto; }
  }, [nota.testo]);

  const salva = (valore) => {
    clearTimeout(timer.current);
    if (valore === salvato.current) return;
    salvato.current = valore;
    onSalva(nota, valore);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const scrivi = (v) => {
    const valore = v.slice(0, MAX_TESTO);
    setTesto(valore);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => salva(valore), 1200);
  };

  return (
    <div className={grande ? "nx-nota-grande" : undefined}
      style={{ background: colore.bg, border: `1px solid ${colore.bordo}`, borderRadius: grande ? 0 : 10, padding: grande ? "14px 16px 10px" : "8px 10px 6px", marginBottom: grande ? 0 : 10,
        boxShadow: "0 2px 6px rgba(0,0,0,.06)", opacity: nota.archiviata ? 0.75 : 1,
        ...(grande ? { position: "fixed", inset: 0, zIndex: 960, display: "flex", flexDirection: "column", overflowY: "auto" } : {}) }}>
      <textarea value={testo} autoFocus={autoFocus} onChange={e => scrivi(e.target.value)} onBlur={() => salva(testo)}
        readOnly={!!nota.archiviata} placeholder="Scrivi qui il tuo appunto…"
        style={{ width: "100%", minHeight: 70, border: "none", background: "transparent", resize: grande ? "none" : "vertical", outline: "none",
          fontSize: grande ? 19 : 13, lineHeight: grande ? 1.5 : 1.45, fontFamily: "'Inter',sans-serif", color: "#1f2937", boxSizing: "border-box",
          textDecoration: nota.archiviata ? "line-through" : "none", ...(grande ? { flex: 1 } : {}) }} />
      <div style={{ display: "flex", alignItems: "center", gap: grande ? 10 : 6, marginTop: 2, flexWrap: "wrap" }}>
        <button type="button" onClick={() => { salva(testo); setGrande(g => !g); }}
          title={grande ? "Riduci" : "Ingrandisci: la nota a tutto schermo con il testo grande"} aria-label={grande ? "Chiudi la nota ingrandita" : "Ingrandisci la nota"}
          style={{ background: grande ? "#fff" : "none", border: grande ? `1px solid ${colore.bordo}` : "none", borderRadius: 6, cursor: "pointer", padding: grande ? "3px 10px" : 2,
            color: "#6b7280", fontSize: grande ? 13 : 14, fontWeight: 700, lineHeight: 1, fontFamily: "'Inter',sans-serif" }}>
          {grande ? "⤡ Riduci" : "⤢"}
        </button>
        {!nota.archiviata && Object.entries(COLORI).map(([k, c]) => (
          <button key={k} title={NOMI_COLORE[k] || k} aria-label={NOMI_COLORE[k] || k} onClick={() => onAggiorna(nota, { colore: k })}
            style={{ width: 14, height: 14, borderRadius: "50%", background: c.bg, cursor: "pointer", padding: 0,
              border: `2px solid ${nota.colore === k || (!nota.colore && k === "giallo") ? "#6b7280" : c.bordo}` }} />
        ))}
        <span style={{ flex: 1, fontSize: 10, color: "#9ca3af", textAlign: "right" }}>{quando(nota.updatedAt)}</span>
        {!nota.archiviata && (
          <button title={nota.fissata ? "Togli dalla cima" : "Tieni in cima"} onClick={() => onAggiorna(nota, { fissata: !nota.fissata })}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: nota.fissata ? "#7c3aed" : "#9ca3af" }}>
            <Pin size={14} fill={nota.fissata ? "#7c3aed" : "none"} />
          </button>
        )}
        <button title={nota.archiviata ? "Rimetti tra le note" : "Fatto — archivia"} onClick={() => { salva(testo); onAggiorna(nota, { archiviata: !nota.archiviata, fissata: false }); }}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: "#059669" }}>
          {nota.archiviata ? <RotateCcw size={14} /> : <Check size={15} />}
        </button>
        <button title="Elimina la nota" onClick={() => onElimina(nota)}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: "#f87171" }}>
          <Trash2 size={14} />
        </button>
      </div>
      {sotto}
      <PromemoriaNota nota={nota} />
    </div>
  );
}

export default function NoteCommessa({ projectId, projectName, orgId, codiceCommessa = "", onNavigate = null }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const uid = currentUser?.uid;
  const [note, setNote] = useState([]);
  const [aperto, setAperto] = useState(false);
  const [vediArchiviate, setVediArchiviate] = useState(false);
  const [nuovaId, setNuovaId] = useState(null);

  useEffect(() => {
    if (!uid || !projectId) return;
    // Il filtro per uid è obbligatorio: le regole rifiutano qualsiasi lettura di note altrui
    const q = query(collection(db, "noteAppunti"), where("uid", "==", uid), where("projectId", "==", projectId));
    return onSnapshot(q, s => setNote(s.docs.map(d => ({ id: d.id, ...d.data({ serverTimestamps: "estimate" }) }))), () => setNote([]));
  }, [uid, projectId]);

  const errore = (e) => addToast("Nota non salvata: " + (e?.message || e), "error");

  const nuova = async () => {
    // Una nota vuota già pronta? Si usa quella invece di crearne un'altra
    const vuota = note.find(n => !n.archiviata && !(n.testo || "").trim());
    if (vuota) { setNuovaId(null); setTimeout(() => setNuovaId(vuota.id), 0); return; }
    try {
      const ref = await addDoc(collection(db, "noteAppunti"), {
        uid, orgId, projectId, projectName: projectName || "", codiceCommessa: codiceCommessa || "", testo: "", colore: "giallo", fissata: false, archiviata: false,
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      setNuovaId(ref.id);
      setVediArchiviate(false);
    } catch (e) { errore(e); }
  };
  const salvaTesto = (n, testo) => updateDoc(doc(db, "noteAppunti", n.id), { testo, updatedAt: serverTimestamp() }).catch(errore);
  const aggiorna = (n, campi) => updateDoc(doc(db, "noteAppunti", n.id), { ...campi, updatedAt: serverTimestamp() }).catch(errore);
  const elimina = async (n) => {
    if ((n.testo || "").trim() && !await confirm("Eliminare questa nota? Non si potrà recuperare.", { type: "danger", title: "Elimina nota", confirmLabel: "Elimina" })) return;
    deleteDoc(doc(db, "noteAppunti", n.id)).catch(errore);
  };

  const chiudi = () => { setAperto(false); setNuovaId(null); };
  // Aprendo il pannello, le note lasciate vuote da più di un minuto si tolgono da sole
  const apri = () => {
    const limite = Date.now() - 60000;
    note.filter(n => !(n.testo || "").trim() && !n.archiviata && ms(n.createdAt) && ms(n.createdAt) < limite)
      .forEach(n => deleteDoc(doc(db, "noteAppunti", n.id)).catch(() => {}));
    setAperto(true);
  };

  const attive = note.filter(n => !n.archiviata).sort(ordina);
  const archiviate = note.filter(n => n.archiviata).sort(ordina);
  const conTesto = attive.filter(n => (n.testo || "").trim()).length;

  return (
    <>
      <button onClick={() => aperto ? chiudi() : apri()} title="Appunti personali su questa commessa: li vedi solo tu"
        style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 11px", borderRadius: 8, border: "1px solid #fde047",
          background: "#fef9c3", color: "#854d0e", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter',sans-serif", flexShrink: 0 }}>
        📝 Le mie note{conTesto > 0 ? ` (${conTesto})` : ""}
      </button>

      {aperto && (
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(380px, 100vw)", background: "#fff", zIndex: 1200,
          boxShadow: "-8px 0 30px rgba(0,0,0,.15)", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid #f3f4f6" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>📝 Le mie note</div>
              <button onClick={chiudi} title="Chiudi" style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280" }}><X size={18} /></button>
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{projectName}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#5b21b6", background: "#f5f3ff",
              border: "1px solid #ddd6fe", borderRadius: 7, padding: "5px 8px", marginTop: 8 }}>
              <Lock size={12} /> Visibili solo a te: nessun altro utente può leggerle, nemmeno il titolare.
            </div>
            <button onClick={nuova} className="btn btn-primary" style={{ marginTop: 10, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
              <Plus size={14} /> Nuova nota
            </button>
            {onNavigate && (
              <button onClick={() => { chiudi(); onNavigate({ page: "registro-note" }); }}
                style={{ marginTop: 8, background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 12, fontWeight: 600, color: "#7c3aed", fontFamily: "'Inter',sans-serif" }}>
                Apri il Registro delle mie note →
              </button>
            )}
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
            {attive.length === 0 && (
              <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 12, padding: "30px 10px", lineHeight: 1.6 }}>
                Nessuna nota su questa commessa.<br />Usale per le cose da ricordare che non sono attività.
              </div>
            )}
            {attive.map(n => (
              <PostIt key={n.id + (n.id === nuovaId ? "-n" : "")} nota={n} autoFocus={n.id === nuovaId} onSalva={salvaTesto} onAggiorna={aggiorna} onElimina={elimina} />
            ))}
            {archiviate.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <button onClick={() => setVediArchiviate(v => !v)}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#6b7280", padding: 0, fontFamily: "'Inter',sans-serif" }}>
                  {vediArchiviate ? "▾" : "▸"} Fatte / archiviate ({archiviate.length})
                </button>
                {vediArchiviate && <div style={{ marginTop: 8 }}>
                  {archiviate.map(n => (
                    <PostIt key={n.id} nota={n} onSalva={salvaTesto} onAggiorna={aggiorna} onElimina={elimina} />
                  ))}
                </div>}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
